import { createSlice } from '@reduxjs/toolkit';
import {
  addItem,
  addItemProduct,
  addItemSupply,
  fetchPackingOrder,
  fetchPackingQueue,
  printPackingLabels,
  removeItem,
  removeItemSupply,
  requestRelabel,
  saveItem,
  scanPackingCode,
  sendToCourier,
} from './operatorPackingThunks';
import {
  courierWaitEnded,
  courierWaitStarted,
  packingItemOpened,
  packingItemOpening,
  packingResolutionDismissed,
} from './operatorPackingActions';
import { canSendPacking, packingOrderOf, printableLabels } from './operatorPackingUtils';
import { describeCandidates } from '../operator/operatorCandidates';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { intentOf } from '../operator/operatorIdempotency';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/*
 * Packing on the floor: the queue of picked orders, and the order being
 * packed — its items (the floor's «bultos»), the one the scans go to, the
 * write this visit waits for and the PDF of its labels. What is out or
 * unconfirmed is kept by order, apart from the visit: it outlives a trip to
 * another order.
 */
/** `receivedAt`: when its first page last came, by the phone's clock. */
const initialQueue = {
  items: [],
  count: 0,
  page: 1,
  hasMore: false,
  loading: false,
  error: null,
  requestId: null,
  receivedAt: null,
};

/** The PDF of the order's labels, merged on the phone: the last one this visit asked for. */
const initialLabelsPrint = {
  requestId: null,
  /** idle | pending | done | failed */
  status: 'idle',
  /** The blob URL of the PDF. */
  url: null,
  opened: false,
  included: 0,
  total: 0,
  /** By `item_index`: the labels that could not be read, and the items labelled only in ZPL. */
  unreadable: [],
  zplOnly: [],
};

/** One visit to an order: opening another starts it over, and a trip to the queue does not. */
const initialTask = {
  orderId: null,
  order: null,
  loading: false,
  error: null,
  requestId: null,
  currentItemId: null,
  scan: { lastCode: null },
  /** The `requestId` of the read being worked out, or `null`. */
  scanning: null,
  /** An addition opening the order's first item: the two go as one step. */
  opening: false,
  /**
   * `{kind, requestId}` of the write this visit sent and waits for, the only
   * answer it takes; kind ∈ addItem | saveItem | addProduct | removeItem |
   * addSupply | removeSupply.
   */
  pendingWrite: null,
  resolution: null,
  /** The last product added, for "+1". */
  lastProduct: null,
  labelsPrint: initialLabelsPrint,
  /**
   * Sending the order to its courier. `lastWrite`, `{type, arg, key}` of the
   * send this visit waits for, kept after a lost answer so that it goes again
   * under its key; `labelWait`, `{orderId, until}` while the screen reads the
   * order for its labels; `waited`, whether this visit already waited for an
   * attempt it found out: once per visit, so the wait's own reads do not start
   * it again; `refusal`, `{orderId, message}` of the last send the server
   * turned down, shown under the button.
   */
  courierSend: { lastWrite: null, labelWait: null, waited: false, refusal: null },
  /**
   * Creating the shipment again, as the packer asks for it once the floor
   * changed the items of a labeled order: `lastWrite` and `refusal` as in
   * `courierSend`. Its wait for the new label is `courierSend.labelWait`.
   */
  relabel: { lastWrite: null, refusal: null },
};

const initialState = {
  queue: initialQueue,
  /** The blob URL of the last labels PDF made, of whichever visit: the next one revokes it. */
  labelsPdfUrl: null,
  /** By order, the `requestId` of its write still out, whichever visit sent it. */
  inFlightByOrder: {},
  /**
   * By order, `{type, arg}` of a write left unconfirmed — its answer lost, or
   * a failure worth retrying — kept to go again as it was. Until it settles
   * nothing else of that order goes, and a reload does not settle it: the
   * lost one may still land after.
   */
  unconfirmed: {},
  ...initialTask,
};

/** The item the scans go to, kept only while it still exists. */
const keepCurrentItem = (state, preferred) => {
  const items = state.order?.items || [];
  const wanted = preferred ?? state.currentItemId;
  state.currentItemId = items.some((item) => item.id === wanted) ? wanted : items[items.length - 1]?.id ?? null;
};

/** Whether an answer is to the write this visit waits for: one sent on an earlier visit is not. */
const isAwaited = (state, action) => state.pendingWrite?.requestId === action.meta.requestId;

/** The write of an order is no longer out. */
const landed = (state, action) => {
  const { orderId } = action.meta.arg;
  if (state.inFlightByOrder[orderId] === action.meta.requestId) delete state.inFlightByOrder[orderId];
};

/**
 * Shows the order as a read or a write brought it. A labels PDF is of the
 * labels it merged: if they changed, it goes. An attempt to send it to its
 * courier on screen settles a send whose answer was lost: what happened is
 * known, and a new tap is a new action, with a key of its own. So does an
 * attempt to create its shipment again.
 */
const showOrder = (state, order) => {
  const before = printableLabels(state.order?.labels).urls;
  const after = printableLabels(order?.labels).urls;
  state.order = order;
  if (before.length !== after.length || before.some((url, index) => url !== after[index])) {
    state.labelsPrint = initialLabelsPrint;
  }
  if (order?.courier_send) state.courierSend.lastWrite = null;
  if (order?.relabel?.requested_at) state.relabel.lastWrite = null;
};

const WRITES = [
  [addItem, 'addItem'],
  [saveItem, 'saveItem'],
  [addItemProduct, 'addProduct'],
  [removeItem, 'removeItem'],
  [addItemSupply, 'addSupply'],
  [removeItemSupply, 'removeSupply'],
];

const operatorPackingSlice = createSlice({
  name: 'operatorPacking',
  initialState,
  reducers: {
    packingEntered: (state, action) => {
      // Another order starts the visit over, `waited` included; written after `Object.assign` it would
      // land on `initialTask`'s own frozen object.
      if (state.orderId !== action.payload) Object.assign(state, initialTask, { orderId: action.payload });
      else state.courierSend.waited = false;
      state.resolution = null;
    },
    packingLeft: (state) => {
      state.resolution = null;
    },
    packingItemFocused: (state, action) => {
      keepCurrentItem(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // The last labels PDF stays known, for the next one to revoke.
      .addCase(operatorWarehouseSelected, (state) => ({ ...initialState, labelsPdfUrl: state.labelsPdfUrl }))
      .addCase(packingResolutionDismissed, (state) => {
        state.resolution = null;
      })
      .addCase(packingItemOpening, (state, action) => {
        if (action.payload === state.orderId) state.opening = true;
      })
      .addCase(packingItemOpened, (state, action) => {
        if (action.payload === state.orderId) state.opening = false;
      })

      .addCase(fetchPackingQueue.pending, (state, action) => {
        state.queue.requestId = action.meta.requestId;
        if (isBackgroundRequest(action)) return;
        state.queue.loading = true;
        state.queue.error = null;
      })
      .addCase(fetchPackingQueue.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.queue.requestId) return;
        const page = action.meta.arg?.page ?? 1;
        const results = action.payload.results || [];
        state.queue.loading = false;
        state.queue.error = null;
        state.queue.page = page;
        state.queue.items = page === 1 ? results : [...state.queue.items, ...results];
        state.queue.count = action.payload.count ?? state.queue.items.length;
        state.queue.hasMore = Boolean(action.payload.next);
        if (page === 1) state.queue.receivedAt = action.meta.receivedAt;
      })
      .addCase(fetchPackingQueue.rejected, (state, action) => {
        // A refresh in the background fails in silence: the list stays as it was, and the next one tries again.
        if (action.meta.requestId !== state.queue.requestId || isBackgroundRequest(action)) return;
        state.queue.loading = false;
        state.queue.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(fetchPackingOrder.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchPackingOrder.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        showOrder(state, action.payload);
        keepCurrentItem(state);
      })
      .addCase(fetchPackingOrder.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(scanPackingCode.pending, (state, action) => {
        state.scanning = action.meta.requestId;
      })
      .addCase(scanPackingCode.fulfilled, (state, action) => {
        if (state.scanning !== action.meta.requestId) return;
        state.scanning = null;
        state.scan.lastCode = acceptedCode(action);
        if (action.payload.kind === 'choose') {
          state.resolution = {
            kind: 'ambiguous',
            origin: 'local',
            message: 'Ese código es de más de un producto: elige cuál.',
            code: action.payload.code,
            options: action.payload.options,
          };
        }
      })
      .addCase(scanPackingCode.rejected, (state, action) => {
        if (state.scanning === action.meta.requestId) state.scanning = null;
      })

      .addCase(printPackingLabels.pending, (state, action) => {
        state.labelsPrint.requestId = action.meta.requestId;
        state.labelsPrint.status = 'pending';
      })
      .addCase(printPackingLabels.fulfilled, (state, action) => {
        const { url, opened, included, total, unreadable, zplOnly } = action.payload;
        if (url) state.labelsPdfUrl = url;
        if (state.labelsPrint.requestId !== action.meta.requestId) return;
        // Not made: its order was no longer on screen once the labels were in.
        if (!url) {
          state.labelsPrint = initialLabelsPrint;
          return;
        }
        state.labelsPrint = {
          requestId: action.meta.requestId,
          status: 'done',
          url,
          opened,
          included,
          total,
          unreadable,
          zplOnly,
        };
      })
      .addCase(printPackingLabels.rejected, (state, action) => {
        if (action.meta.condition || state.labelsPrint.requestId !== action.meta.requestId) return;
        state.labelsPrint = { ...initialLabelsPrint, requestId: action.meta.requestId, status: 'failed' };
      })

      // Out, the send keeps the order's other writes back, as any write of it does.
      .addCase(sendToCourier.pending, (state, action) => {
        const { orderId } = action.meta.arg;
        state.inFlightByOrder[orderId] = action.meta.requestId;
        if (orderId !== state.orderId) return;
        state.courierSend.refusal = null;
        state.courierSend.lastWrite = {
          type: sendToCourier.typePrefix,
          arg: intentOf(action.meta.arg),
          key: action.meta.requestId,
        };
      })
      .addCase(sendToCourier.fulfilled, (state, action) => {
        landed(state, action);
        if (state.courierSend.lastWrite?.key !== action.meta.requestId) return;
        state.courierSend.lastWrite = null;
        if (state.order?.id !== action.meta.arg.orderId) return;
        // A reload sent before this answer would bring the order back without its attempt.
        if (state.loading) {
          state.loading = false;
          state.requestId = null;
        }
        showOrder(state, packingOrderOf(action.payload));
      })
      .addCase(sendToCourier.rejected, (state, action) => {
        if (action.meta.condition) return;
        landed(state, action);
        // A lost answer keeps its key for the next tap; a refusal lets the next one be new.
        if (state.courierSend.lastWrite?.key === action.meta.requestId && !isRetryable(action.payload)) {
          state.courierSend.lastWrite = null;
        }
        const { orderId } = action.meta.arg;
        if (!action.meta.offScreenTask && orderId === state.orderId && !isRetryable(action.payload)) {
          state.courierSend.refusal = { orderId, message: action.payload?.message || null };
        }
      })
      // As a send: out, it keeps the order's other writes back.
      .addCase(requestRelabel.pending, (state, action) => {
        const { orderId } = action.meta.arg;
        state.inFlightByOrder[orderId] = action.meta.requestId;
        if (orderId !== state.orderId) return;
        state.relabel.refusal = null;
        state.relabel.lastWrite = {
          type: requestRelabel.typePrefix,
          arg: intentOf(action.meta.arg),
          key: action.meta.requestId,
        };
      })
      .addCase(requestRelabel.fulfilled, (state, action) => {
        landed(state, action);
        if (state.relabel.lastWrite?.key !== action.meta.requestId) return;
        state.relabel.lastWrite = null;
        if (state.order?.id !== action.meta.arg.orderId) return;
        if (state.loading) {
          state.loading = false;
          state.requestId = null;
        }
        showOrder(state, packingOrderOf(action.payload));
      })
      .addCase(requestRelabel.rejected, (state, action) => {
        if (action.meta.condition) return;
        landed(state, action);
        if (state.relabel.lastWrite?.key === action.meta.requestId && !isRetryable(action.payload)) {
          state.relabel.lastWrite = null;
        }
        const { orderId } = action.meta.arg;
        if (!action.meta.offScreenTask && orderId === state.orderId && !isRetryable(action.payload)) {
          state.relabel.refusal = { orderId, message: action.payload?.message || null };
        }
      })
      .addCase(courierWaitStarted, (state, action) => {
        if (action.payload.orderId !== state.orderId) return;
        state.courierSend.labelWait = action.payload;
        state.courierSend.waited = true;
      })
      .addCase(courierWaitEnded, (state, action) => {
        const { labelWait } = state.courierSend;
        if (labelWait?.orderId === action.payload.orderId && labelWait.until === action.payload.until) {
          state.courierSend.labelWait = null;
        }
      });

    WRITES.forEach(([thunk, kind]) => {
      builder
        .addCase(thunk.pending, (state, action) => {
          const { orderId } = action.meta.arg;
          state.inFlightByOrder[orderId] = action.meta.requestId;
          if (orderId !== state.orderId) return;
          state.pendingWrite = { kind, requestId: action.meta.requestId };
          state.resolution = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          landed(state, action);
          // Whichever write of the order answers settles it: nothing else of it went meanwhile.
          delete state.unconfirmed[action.meta.arg.orderId];
          if (!isAwaited(state, action)) return;
          state.pendingWrite = null;
          if (!state.order) return;
          state.error = null;
          // A reload sent before this answer would bring back what it changed.
          if (state.loading) {
            state.loading = false;
            state.requestId = null;
          }
          showOrder(state, packingOrderOf(action.payload));
          if (kind === 'addProduct') {
            const { productCode, productId, lineId, sku, factor } = action.meta.arg;
            state.lastProduct = { productCode, productId, lineId, sku, factor };
          }
          // A replayed item answers with the one it had made.
          keepCurrentItem(state, kind === 'addItem' ? action.payload.item_id : undefined);
        })
        .addCase(thunk.rejected, (state, action) => {
          if (action.meta.condition) return;
          landed(state, action);
          const { orderId } = action.meta.arg;
          const error = action.payload;
          // The very arg: the thunk's condition lets only it through while this is kept.
          if (isRetryable(error)) state.unconfirmed[orderId] = { type: thunk.typePrefix, arg: action.meta.arg };
          else delete state.unconfirmed[orderId];
          if (!isAwaited(state, action)) return;
          state.pendingWrite = null;
          if (error?.kind === OPERATOR_ERROR.AMBIGUOUS && kind === 'addProduct') {
            state.resolution = {
              kind: 'ambiguous',
              origin: 'server',
              message: error.message,
              ...describeCandidates(error.candidates),
              arg: action.meta.arg,
            };
          }
        });
    });
  },
});

export const { packingEntered, packingLeft, packingItemFocused } = operatorPackingSlice.actions;

/** Whether a read code is welcome right now; see `canSendPacking`. */
export const selectPackingAccepting = (state) => {
  const packing = state.operatorPacking;
  return canSendPacking(packing) && !packing.resolution && !packing.scanning;
};

export default operatorPackingSlice.reducer;
